import React, { useState, useEffect } from 'react';
import { Plus, Search, Building2, Pencil, Trash2, Star, CreditCard, Palette } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../hooks/use-toast';
import BackButton from '../../../../components/admin/BackButton';
import CompanyFormModal from '../../../../components/admin/procurement/CompanyFormModal';
import BankAccountsManager from '../../../../components/admin/procurement/BankAccountsManager';
import BrandPreviewModal from '../../../../components/admin/procurement/BrandPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [selectedCompanyForBanks, setSelectedCompanyForBanks] = useState(null);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [selectedCompanyForBrand, setSelectedCompanyForBrand] = useState(null);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/procurement/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch companies');
      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast({
        title: 'Error',
        description: 'Failed to load companies',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setIsEditModalOpen(true);
  };

  const handleManageBanks = (company) => {
    setSelectedCompanyForBanks(company);
    setIsBankModalOpen(true);
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;

    try {
      const response = await fetch(`${API_URL}/api/procurement/companies/${companyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete company');
      }

      toast({ title: 'Success', description: 'Company deleted successfully' });
      fetchCompanies();
    } catch (error) {
      console.error('Error deleting company:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <Building2 className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Companies</h2>
              <p className="text-sm text-[#6C757D]">Manage issuing companies for POs and invoices</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        </div>
      </div>

      {/* Companies List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading companies...</p>
          </CardContent>
        </Card>
      ) : companies.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">No companies yet. Add your first company!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {companies.map((company) => (
            <Card key={company.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header with logo */}
                    <div className="flex items-center space-x-4 mb-4">
                      {company.logo_base64 && (
                        <img
                          src={company.logo_base64}
                          alt={company.legal_name}
                          className="h-16 w-16 object-contain rounded-lg border border-gray-200 p-2 bg-white"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          <Building2 className="h-5 w-5 text-[#00B4D8]" />
                          <h3 className="text-lg font-semibold text-[#2B2D42]">
                            {company.legal_name}
                          </h3>
                          {company.is_default && (
                            <Badge className="bg-green-100 text-green-800">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                          <Badge className={company.is_active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                            {company.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        {company.trading_name && (
                          <p className="text-sm text-[#6C757D]">Trading as: {company.trading_name}</p>
                        )}
                      </div>
                    </div>

                    {/* Company Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Address</p>
                        <p className="text-sm text-[#2B2D42]">
                          {company.address}
                          {company.city && `, ${company.city}`}
                          {company.state && `, ${company.state}`}
                        </p>
                        <p className="text-sm text-[#2B2D42]">
                          {company.country}
                          {company.postal_code && ` ${company.postal_code}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Contact</p>
                        <p className="text-sm text-[#2B2D42]">{company.phone}</p>
                        <p className="text-sm text-[#2B2D42]">{company.email}</p>
                        {company.website && (
                          <a
                            href={company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#00B4D8] hover:underline"
                          >
                            {company.website}
                          </a>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Registration</p>
                        <p className="text-sm text-[#2B2D42]">Reg: {company.registration_number}</p>
                        {company.vat_number && (
                          <p className="text-sm text-[#2B2D42]">VAT: {company.vat_number}</p>
                        )}
                      </div>
                    </div>

                    {/* Bank Accounts */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <CreditCard className="h-4 w-4 text-[#6C757D]" />
                          <span className="text-sm text-[#6C757D]">
                            {company.bank_accounts?.length || 0} Bank Account(s)
                          </span>
                        </div>
                        <Button
                          onClick={() => handleManageBanks(company)}
                          variant="outline"
                          size="sm"
                          className="text-[#00B4D8] border-[#00B4D8] hover:bg-[#00B4D8] hover:text-white"
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Manage Accounts
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col space-y-2 ml-4">
                    <Button
                      onClick={() => { setSelectedCompanyForBrand(company); setIsBrandModalOpen(true); }}
                      variant="outline"
                      size="sm"
                      className="border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white"
                      data-testid={`brand-preview-btn-${company.id}`}
                      title="Brand & PDF preview"
                    >
                      <Palette className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleEdit(company)}
                      variant="outline"
                      size="sm"
                      className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8] hover:text-white"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!company.is_default && (
                      <Button
                        onClick={() => handleDelete(company.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <CompanyFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          fetchCompanies();
          setIsCreateModalOpen(false);
        }}
      />
      <CompanyFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
        onSuccess={() => {
          fetchCompanies();
          setIsEditModalOpen(false);
          setSelectedCompany(null);
        }}
      />
      {selectedCompanyForBanks && (
        <BankAccountsManager
          isOpen={isBankModalOpen}
          onClose={() => {
            setIsBankModalOpen(false);
            setSelectedCompanyForBanks(null);
          }}
          company={selectedCompanyForBanks}
          onSuccess={fetchCompanies}
        />
      )}
      <BrandPreviewModal
        open={isBrandModalOpen}
        company={selectedCompanyForBrand}
        token={token}
        onClose={() => { setIsBrandModalOpen(false); setSelectedCompanyForBrand(null); }}
        onSaved={(updated) => {
          fetchCompanies();
          setSelectedCompanyForBrand(updated);
        }}
      />
    </div>
  );
};

export default Companies;